import { useState, useEffect, useRef, useCallback } from "react";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import { markActivity, getLastActivity, getInFlightCount } from "@/lib/idleTracker";

const WARNING_SECONDS = 60;

export default function IdleSessionManager({ timeoutMinutes }) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);
  const warningRef = useRef(false);
  const loggingOutRef = useRef(false);
  const lastKeepaliveRef = useRef(Date.now());

  const doLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    try { await authAPI.logout(); } catch (e) { /* session may already be invalid */ }
    try { localStorage.clear(); sessionStorage.clear(); } catch (e) { /* ignore */ }
    window.location.href = "/login?expired=1";
  }, []);

  const stayConnected = useCallback(() => {
    markActivity(true);
    warningRef.current = false;
    setWarning(false);
    authAPI.keepalive().catch(() => {});
  }, []);

  useEffect(() => {
    if (!timeoutMinutes) return undefined;
    const timeoutMs = timeoutMinutes * 60 * 1000;

    const onActivity = () => {
      if (warningRef.current) return; // modal open: explicit button (or another tab) required
      markActivity();
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "wheel"]; // mousemove/mouseover excluded: too weak a signal
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    markActivity(true);

    const tick = setInterval(() => {
      if (loggingOutRef.current) return;
      const remaining = timeoutMs - (Date.now() - getLastActivity());
      if (remaining <= 0) {
        if (getInFlightCount() > 0) return; // never logout while a request is in-flight
        doLogout();
      } else if (remaining <= WARNING_SECONDS * 1000) {
        setSecondsLeft(Math.max(0, Math.ceil(remaining / 1000)));
        warningRef.current = true;
        setWarning(true);
      } else if (warningRef.current) {
        warningRef.current = false; // activity in another tab closed the modal
        setWarning(false);
      }
    }, 1000);

    const keepaliveMs = Math.min(5 * 60 * 1000, timeoutMs / 2);
    const keepalive = setInterval(() => {
      if (loggingOutRef.current) return;
      if (getLastActivity() > lastKeepaliveRef.current) {
        lastKeepaliveRef.current = Date.now();
        authAPI.keepalive().catch(() => {});
      }
    }, keepaliveMs);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(tick);
      clearInterval(keepalive);
    };
  }, [timeoutMinutes, doLogout]);

  if (!warning) return null;

  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="idle-warning-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Clock size={20} className="text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Êtes-vous toujours là ?</h3>
        </div>
        <p className="text-sm text-slate-600">
          Pour votre sécurité, vous serez déconnecté dans{" "}
          <span className="font-bold text-amber-600 tabular-nums" data-testid="idle-countdown">{secondsLeft}</span>{" "}
          seconde{secondsLeft > 1 ? "s" : ""} en raison d'une inactivité prolongée.
        </p>
        <Button
          onClick={stayConnected}
          className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
          data-testid="idle-stay-connected-btn"
        >
          Je suis toujours là
        </Button>
      </div>
    </div>
  );
}
